export interface CheckoutAttributionContext {
  source?: string | null;
  sourcePath?: string | null;
  paywallType?: string | null;
  paywallFeature?: string | null;
  contentId?: string | null;
  contentTitle?: string | null;
  returnPath?: string | null;
  provider?: string | null;
  tier?: string | null;
  /** The exact trial or promotional offer presented by the checkout surface. */
  offerKind?: string | null;
  /**
   * VIP/Pro only: the server-quoted price the paywall showed (major units),
   * so the hosted checkout return reports the real amount, not a Premium price.
   */
  quotedValue?: number | null;
  quotedCurrency?: string | null;
}

const CHECKOUT_ATTRIBUTION_STORAGE_KEY = 'jp:checkout-attribution';

function normalizeOptionalString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeCheckoutAttributionContext(
  value: unknown
): CheckoutAttributionContext | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  const normalized: CheckoutAttributionContext = {
    source: normalizeOptionalString(record.source),
    sourcePath: normalizeOptionalString(record.sourcePath),
    paywallType: normalizeOptionalString(record.paywallType),
    paywallFeature: normalizeOptionalString(record.paywallFeature),
    contentId: normalizeOptionalString(record.contentId),
    contentTitle: normalizeOptionalString(record.contentTitle),
    returnPath: normalizeOptionalString(record.returnPath),
    provider: normalizeOptionalString(record.provider),
    tier: normalizeOptionalString(record.tier),
    offerKind: normalizeOptionalString(record.offerKind),
    quotedValue:
      typeof record.quotedValue === 'number' && Number.isFinite(record.quotedValue) && record.quotedValue > 0
        ? record.quotedValue
        : null,
    quotedCurrency: normalizeOptionalString(record.quotedCurrency),
  };

  return Object.values(normalized).some(Boolean) ? normalized : null;
}

export function storeCheckoutAttributionContext(
  context: CheckoutAttributionContext
): void {
  if (typeof window === 'undefined') return;

  const normalized = normalizeCheckoutAttributionContext(context);
  if (!normalized) return;

  try {
    window.sessionStorage.setItem(
      CHECKOUT_ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(normalized)
    );
  } catch {
    // Ignore storage failures in private mode or restricted browsers.
  }
}

export function getCheckoutAttributionContext(): CheckoutAttributionContext | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    return normalizeCheckoutAttributionContext(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function clearCheckoutAttributionContext(): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(CHECKOUT_ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Ignore storage failures in private mode or restricted browsers.
  }
}
